import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, Sparkles, Rocket, BookOpen } from "lucide-react";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";

const PLAN_META = {
  free:   { icon: BookOpen, tagline: "Try the workflow, publish one manuscript a month",  accent: "border-slate-200" },
  author: { icon: Sparkles, tagline: "The complete solo-author toolkit",                  accent: "border-emerald-400 ring-2 ring-emerald-100" },
  pro:    { icon: Crown,    tagline: "Ghostwriters + small publishing teams",             accent: "border-amber-400 ring-2 ring-amber-100" },
  studio: { icon: Rocket,   tagline: "Boutique publishers + serious volume",              accent: "border-indigo-400" },
};

const FEATURES = {
  free:   ["1 book / month", "Watermarked PDF export only", "No AI hero images", "1 workspace, 1 seat"],
  author: ["10 books / month", "30 AI hero images", "Watermark-free EPUB · PDF · DOCX", "1 workspace · 2 seats", "Publisher's Kit + Preset library", "Overage: +$1.99/book · +$0.30/image"],
  pro:    ["40 books / month", "150 AI hero images", "3 workspaces · 10 seats", "Priority formatting queue", "Shared preset library across the team", "Overage: +$1.49/book · +$0.20/image"],
  studio: ["200 books / month", "500 AI hero images", "Unlimited workspaces + seats", "White-label EPUB / PDF exports", "Priority support · SLA", "Overage: +$0.99/book · +$0.15/image"],
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [busyKey, setBusyKey] = useState(null);
  const [me, setMe] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing/plans");
        setPlans(data.plans || []);
      } catch (err) { toast.error(formatApiError(err)); }
      if (user) {
        try {
          const { data } = await api.get("/pricing/me");
          setMe(data);
        } catch {}
      }
    })();
  }, [user]);

  const subscribe = async (plan) => {
    if (!user) { navigate(`/login?redirect=/pricing`); return; }
    if (plan.plan_key === "free") { toast.success("You're on the Free plan"); return; }
    const lookup_key = interval === "yearly" ? plan.yearly_lookup_key : plan.monthly_lookup_key;
    setBusyKey(plan.plan_key);
    try {
      const { data } = await api.post("/pricing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusyKey(null);
    }
  };

  const price = (plan) => {
    const p = interval === "yearly" ? plan.yearly_price : plan.monthly_price;
    if (!p) return null;
    const dollars = (p.amount / 100).toFixed(0);
    return dollars;
  };

  return (
    <div className="min-h-screen bg-[color:var(--canvas)]">
      <Nav/>
      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[color:var(--text)]" data-testid="pricing-title">
            Pricing that scales with your publishing pace
          </h1>
          <p className="mt-4 text-base text-[color:var(--text-muted)] max-w-2xl mx-auto">
            Every plan includes unlimited exports and unlimited re-uploads on the same book —
            you pay per manuscript, not per keystroke.
          </p>
          {/* Monthly / yearly toggle */}
          <div className="mt-8 inline-flex border border-[color:var(--border)] bg-white" data-testid="pricing-interval-toggle">
            <button
              onClick={()=>setInterval("monthly")}
              className={`px-5 py-2 text-sm ${interval === "monthly" ? "bg-[color:var(--text)] text-white" : "text-[color:var(--text-muted)]"}`}
              data-testid="pricing-interval-monthly">Monthly</button>
            <button
              onClick={()=>setInterval("yearly")}
              className={`px-5 py-2 text-sm ${interval === "yearly" ? "bg-[color:var(--text)] text-white" : "text-[color:var(--text-muted)]"}`}
              data-testid="pricing-interval-yearly">
              Yearly <span className="ml-2 text-xs uppercase tracking-widest text-emerald-600">2 months free</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
          {plans.map((plan) => {
            const meta = PLAN_META[plan.plan_key];
            const Icon = meta.icon;
            const dollars = price(plan);
            const isCurrent = me && me.plan_key === plan.plan_key;
            return (
              <div key={plan.plan_key}
                   className={`bg-white border ${meta.accent} p-8 flex flex-col`}
                   data-testid={`pricing-card-${plan.plan_key}`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-6 h-6 text-[color:var(--primary)]" strokeWidth={1.5}/>
                  <h3 className="text-xl font-medium text-[color:var(--text)] capitalize">{plan.plan_key}</h3>
                </div>
                <p className="text-xs text-[color:var(--text-muted)] mb-6 min-h-[36px]">{meta.tagline}</p>
                <div className="mb-6">
                  {dollars !== null ? (
                    <>
                      <span className="text-4xl font-serif text-[color:var(--text)]" data-testid={`pricing-price-${plan.plan_key}`}>
                        ${dollars}
                      </span>
                      <span className="text-sm text-[color:var(--text-muted)] ml-1">
                        /{interval === "yearly" ? "year" : "mo"}
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-serif text-[color:var(--text)]">$0</span>
                  )}
                </div>
                <ul className="space-y-2 mb-8 text-sm text-[color:var(--text)] flex-1">
                  {FEATURES[plan.plan_key].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="text-center text-xs uppercase tracking-widest text-emerald-700 py-3 border border-emerald-300 bg-emerald-50"
                       data-testid={`pricing-current-${plan.plan_key}`}>
                    Current plan
                  </div>
                ) : (
                  <button
                    onClick={()=>subscribe(plan)}
                    disabled={busyKey === plan.plan_key}
                    className={`w-full py-3 text-sm font-medium transition-colors ${
                      plan.plan_key === "free"
                        ? "border border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
                        : "bg-[color:var(--text)] text-white hover:bg-[color:var(--primary)]"
                    }`}
                    data-testid={`pricing-cta-${plan.plan_key}`}
                  >
                    {busyKey === plan.plan_key ? "Redirecting…"
                      : plan.plan_key === "free" ? "Start free"
                      : user ? `Upgrade to ${plan.plan_key}` : `Start ${plan.plan_key}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center text-xs text-[color:var(--text-muted)] max-w-3xl mx-auto space-y-2">
          <p>
            <strong>7-day free trial</strong> on any paid plan · Cancel any time · No hidden fees ·
            Managed billing via Stripe.
          </p>
          <p>
            Bookforge360 processes payments through Stripe Managed Payments — Stripe handles
            tax calculation, collection, filing, and remittance so you don't have to.
          </p>
        </div>
      </main>
      <SiteFooter/>
    </div>
  );
}
